<script setup>
import { withTrailingSlash } from '@/utils/siteUrl'

defineProps({
  name:          { type: String, required: true },  // small label — Figma "Elliot, 26" slot
  quote:         { type: String, required: true },
  image:         { type: Object, required: true },   // { src, alt }
  colour:        { type: String, default: 'pink' },  // pink | cream | lime — text-panel fill
  imagePosition: { type: String, default: 'top' },   // top | bottom — image column left or right of text (>=650px viewport only; ignored below that)
  cta:           { type: Object, required: true },    // { label, to }
  link:          { type: Boolean, default: true },   // false renders a static, non-clickable card with no "Read their story" CTA — Home's teaser row (jk-31 follow-up), which has its own general Academy CTA below the grid
})
</script>

<template>
  <component :is="link ? 'RouterLink' : 'div'" :to="link ? withTrailingSlash(cta.to) : undefined" class="academy-card" :class="[colour, { linked: link }]" :aria-label="link ? cta.label : undefined">
    <div class="layout">
      <!-- DOM order sets the column order (image left vs right) at >=650px
           viewport; ignored below that (image is always the left/top slot). -->
      <template v-if="imagePosition === 'top'">
        <div class="media"><img :src="image.src" :alt="image.alt" loading="lazy" /></div>
        <div class="text">
          <div class="body">
            <p class="name">{{ name }}</p>
            <p class="quote">{{ quote }}</p>
          </div>
          <span v-if="link" class="link">{{ cta.label }} →</span>
        </div>
      </template>
      <template v-else>
        <div class="text">
          <div class="body">
            <p class="name">{{ name }}</p>
            <p class="quote">{{ quote }}</p>
          </div>
          <span v-if="link" class="link">{{ cta.label }} →</span>
        </div>
        <div class="media"><img :src="image.src" :alt="image.alt" loading="lazy" /></div>
      </template>
    </div>
  </component>
</template>

<style lang="scss" scoped>
.academy-card {
  // Whole card is the click target now (matches TrainingCard/NewsCard/
  // OurWorkCard) — the old inner "Learn more" RouterLink is now a plain
  // span (see .link); nesting an <a> inside an <a> isn't valid HTML.
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: default;
  @include rounded($radius);

  // `link="false"` (Home's teaser row) renders a plain, non-clickable <div>
  // — no pointer cursor or hover lift, so it doesn't look interactive when
  // it isn't (jk-34). Cards used with link="true" keep both.
  &.linked {
    cursor: pointer;
    @include transition(box-shadow, transform);

    &:hover         { box-shadow: var(--shadow-2); transform: translateY(-2px); }
    &:focus-visible { @include focus-ring; outline: none; }
  }

  &.pink  { background: var(--color-card-pink); }
  &.cream { background: var(--color-card-cream); }
  &.lime  { background: var(--color-card-lime); }   // related-stories band (Feature bj-24 Phase 2)

  .layout {
    // Default (>=650px viewport): 2 equal columns, image left or right
    // per DOM order above. align-items defaults to `stretch` (not
    // overridden here) — both columns take the row's height, set by
    // whichever is naturally taller (almost always .text). That gives
    // .media a definite height the grid resolves, which its <img> fills
    // via height: 100% below — the image crops to match the text
    // column's height, rather than its own aspect ratio setting the row.
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    // jk-37: when 2 cards sit side-by-side (from-md-lg+, Home's Academy
    // section — the only place 2 of these share a grid row), `.cards`'
    // own `align-items: stretch` already gives each `.academy-card` a
    // shared, definite height (equal to whichever card's content is
    // taller). Without this, `.layout` still only took *its own* content's
    // natural height inside that taller box — so each card's image synced
    // to its own quote length, not to its sibling's, and the two ended up
    // visibly different sizes. `height: 100%` makes `.layout` (and, via its
    // own stretch, `.media`/`.text` inside it) fill the shared height
    // instead — a plain CSS fix, not literally `grid-template-rows:
    // subgrid` (which would need `.academy-card` to become a grid
    // container in its own right, for no extra benefit here: `.cards`
    // already has exactly one row, so there's nothing a second nesting
    // level of subgrid would still need to pass down).
    height: 100%;
  }

  .media {
    justify-self: end;
    @include rounded($radius);
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  .text {
    padding: $space-5;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: $space-5;

    .body {
      display: flex;
      flex-direction: column;
      gap: $space-3;

      .name {
        margin: 0;
        font-size: $font-size-sm;
        line-height: $lh-base;
      }

      .quote {
        margin: 0;
        font-size: $font-size-md;   // one step down from lg (Figma heading-m 32px; shrunk per review, then again per sitewide quote-size review)
        line-height: 1.35;
        letter-spacing: $ls-heading;
      }
    }

    // Secondary CTA — plain text now (the whole card is the real link).
    // Matches ImpactCard's `.link` exactly as of the just-merged jk-18
    // work (feature/jk-18-impact-card-enhancements, merged into this
    // branch mid-session): plain "label →" text, no wrapped arrow span,
    // a light hover-opacity fade on the text itself, with focus/active
    // living on the card root instead (see `.academy-card` above).
    .link {
      align-self: flex-start;
      font-weight: 500;
      color: var(--color-ink);
      text-decoration: underline;
      text-underline-offset: 3px;
      @include transition(opacity);

      &:hover { opacity: .75; }
    }
  }

  // 500-650px VIEWPORT: same 2-column grid, but the image crops to a
  // taller rectangle instead of the default stretch-to-text-height square.
  // 3/4 is a starting value — tune on visual sign-off. Both remaining
  // bands below are narrower still, so this rule stays in effect there
  // too (cascades forward — not re-declared in each subsequent block).
  @media (max-width: 650px) {
    .media img { aspect-ratio: 3 / 4; }
  }

  // 500px – below from-md-lg (920px) — jk-37 direct request: at this width
  // each card gets the whole (single-column, see Home.vue's `.academy
  // .cards`) row to itself, and a plain 50/50 image:text split read as an
  // oversized image next to a modest amount of copy. Text now gets 2
  // shares to the image's 1. Flex, not grid — the ratio then holds
  // regardless of which side `.media` is on (`imagePosition="top"` vs
  // `"bottom"` swaps which child comes first in the DOM, not which
  // flex-grow value it gets). Lower-bounded at 500px (not just `max-width`)
  // so this can never overlap the next block down, which needs `.layout`
  // to stay a *grid* container for its `grid-template-areas` to work.
  @media (min-width: 500px) and (max-width: 919.98px) {
    .layout {
      display: flex;
      align-items: stretch;
    }
    .media { flex: 1; min-width: 0; }
    .text  { flex: 2; min-width: 0; }
  }

  // Below $bp-xs (500px) — jk-37 direct request ("only get an image above
  // xs"): image hidden entirely, name/quote/CTA stack full-width. Was two
  // tiers (350-500px: image shrunk to a 1/3 column; <350px: image hidden
  // entirely) — merged into one now that the "no image" cutoff moved up to
  // meet the old 1/3-column tier's own top edge, leaving no width range for
  // that in-between state to occupy. .text still needs `display: contents`
  // so its children (.body, .link) are promoted to direct grid items of
  // .layout, letting each be placed independently via the named areas below
  // — previously inherited from the tier this merged into, now stated here
  // directly. 499.98px, not $bp-xs itself, so this and a `from-xs` `min-
  // width` rule elsewhere can never both match the exact same 1px.
  @media (max-width: 499.98px) {
    .layout {
      grid-template-columns: 1fr;
      grid-template-areas:
        "body"
        "link";
    }

    .media { display: none; }

    .text {
      display: contents;
      .body {
        padding: $space-5 $space-5 0;
        // `.link` normally supplies the card's bottom padding (below).
        // Home's teaser row (`link="false"`) renders no `.link` at all, so
        // without this, `.body` — now the last child — closes with its own
        // 0 bottom padding and the card loses its bottom edge entirely at
        // this breakpoint. `:last-child` is scoped to `.body`'s real DOM
        // parent (`.text`), unaffected by `.text`'s own `display: contents`
        // — true only when no `.link` sibling exists, so linked cards keep
        // their existing look untouched.
        &:last-child { padding-bottom: $space-5; }
      }
    }

    .link { padding: $space-3 $space-5 $space-5; }
  }
}
</style>
